import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
 UserPlus,
 ArrowLeft,
 Mail,
 User,
 Shield,
 CheckCircle,
 AlertCircle,
 Save
} from 'lucide-react'
import { Button, PasswordInput, Card, CardHeader, CardTitle, CardContent, ModernInput, ModernButton } from '../../components/ui'
import { useUserRoles } from '../../hooks/useUserRoles'
import { useUsers } from '../../hooks/useUsers'
import { UserPhone } from '../../types'

export const UserRegistrationPage: React.FC = () => {
 const navigate = useNavigate()
 const { t } = useTranslation()
 const { id } = useParams<{ id: string }>()
 const location = useLocation()
 const { roles, isLoading: rolesLoading, error: rolesError } = useUserRoles()
 const { createUser } = useUsers()
 const [isSubmitting, setIsSubmitting] = useState(false)
 const [errors, setErrors] = useState<Record<string, string>>({})
 
 // Verifica se está no modo de edição
 const isEditMode = !!id
 const userData = location.state?.userData

 const [formData, setFormData] = useState({
 name: userData?.name || '',
 email: userData?.email || '',
 password: '',
 confirmPassword: '',
 dealershipRole: userData?.dealershipRole || userData?.role?.name || '',
 phone: userData?.phones?.[0]?.phone || '',
 codeArea: userData?.phones?.[0]?.codeArea || '+55',
 area: userData?.phones?.[0]?.area || '',
 isActive: userData?.isActive ?? true
 })

 // Atualiza o formulário quando os dados do usuário mudarem
 useEffect(() => {
 if (userData && isEditMode) {
 setFormData({
 name: userData.name || '',
 email: userData.email || '',
 password: '',
 confirmPassword: '',
 dealershipRole: userData.dealershipRole || userData.role?.name || '',
 phone: userData.phones?.[0]?.phone || '',
 codeArea: userData.phones?.[0]?.codeArea || '+55',
 area: userData.phones?.[0]?.area || '',
 isActive: userData.isActive ?? true
 })
 }
 }, [userData, isEditMode])

 const validateForm = () => {
 const newErrors: Record<string, string> = {}

 if (!formData.name.trim()) {
   newErrors.name = t('userRegistration.validation.nameRequired')
 }

 if (!formData.email.trim()) {
   newErrors.email = t('userRegistration.validation.emailRequired')
 } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
   newErrors.email = t('userRegistration.validation.emailInvalid')
 }

 // Validação de senha só é obrigatória no modo de criação
 if (!isEditMode) {
 if (!formData.password) {
   newErrors.password = t('userRegistration.validation.passwordRequired')
 } else if (formData.password.length < 6) {
   newErrors.password = t('userRegistration.validation.passwordMin')
 }

 if (!formData.confirmPassword) {
   newErrors.confirmPassword = t('userRegistration.validation.confirmPasswordRequired')
 } else if (formData.password !== formData.confirmPassword) {
   newErrors.confirmPassword = t('userRegistration.validation.passwordMismatch')
 }
 } else {
 // No modo de edição, se senha for preenchida, deve ser validada
 if (formData.password && formData.password.length < 6) {
   newErrors.password = t('userRegistration.validation.passwordMin')
 }
 if (formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword) {
   newErrors.confirmPassword = t('userRegistration.validation.passwordMismatch')
 }
 }

 if (!formData.dealershipRole) {
   newErrors.role = t('userRegistration.validation.roleRequired')
 }

 if (!formData.area.trim()) {
   newErrors.area = t('userRegistration.validation.areaRequired')
 }

 if (!formData.phone.trim()) {
   newErrors.phone = t('userRegistration.validation.phoneRequired')
 }

 setErrors(newErrors)
 return Object.keys(newErrors).length === 0
 }

 const handleSubmit = async (e: React.FormEvent) => {
 e.preventDefault()
 
 if (!validateForm()) {
 return
 }

 setIsSubmitting(true)
 
 try {
 const phoneData: UserPhone = {
 codeArea: formData.codeArea,
 area: formData.area,
 phone: formData.phone,
 isPrimary: true
 }

 await createUser({
 name: formData.name,
 email: formData.email,
 password: formData.password,
 phones: [phoneData],
 dealershipRole: formData.dealershipRole,
 isActive: true
 })
 
 // Redireciona para lista de usuários
 navigate('/users', {
   state: {
     successMessage: t('userRegistration.successCreate')
   }
 })
 } catch (error) {
 console.error('Erro ao cadastrar usuário:', error)
 setErrors({ submit: error instanceof Error ? error.message : t('userRegistration.createError') })
 } finally {
 setIsSubmitting(false)
 }
 }

 const handleInputChange = (field: string, value: string) => {
 setFormData(prev => ({ ...prev, [field]: value }))
 // Limpa erro do campo quando usuário começa a digitar
 if (errors[field]) {
 setErrors(prev => ({ ...prev, [field]: '' }))
 }
 }

 const getSelectedRole = () => {
 return roles.find(r => r.value === formData.dealershipRole)
 }

 return (
 <div className="max-w-2xl mx-auto space-y-6">
 {/* Header */}
 <div className="flex items-center space-x-4">
 <Button
 variant="outline"
 onClick={() => navigate('/users')}
 className="flex items-center"
 >
 <ArrowLeft className="h-4 w-4 mr-2" />
 {t('userRegistration.back')}
 </Button>
 <div>
 <h1 className="text-2xl font-bold text-foreground">
 {isEditMode ? t('userRegistration.titleEdit') : t('userRegistration.titleCreate')}
 </h1>
 <p className="text-muted-foreground">
 {isEditMode ? t('userRegistration.subtitleEdit') : t('userRegistration.subtitleCreate')}
 </p>
 </div>
 </div>

 <Card>
 <CardHeader>
 <CardTitle className="flex items-center">
 <UserPlus className="h-5 w-5 mr-2" />
 {t('userRegistration.cardTitle')}
 </CardTitle>
 </CardHeader>
 <CardContent>
 <form onSubmit={handleSubmit} className="space-y-6">
 {/* Error Message */}
 {errors.submit && (
 <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
 <div className="flex items-center">
 <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mr-3 flex-shrink-0" />
 <p className="text-sm text-red-700 dark:text-red-300">
 {errors.submit}
 </p>
 </div>
 </div>
 )}

 {/* Roles Error */}
 {rolesError && (
 <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
 <div className="flex items-center">
 <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mr-3 flex-shrink-0" />
 <p className="text-sm text-yellow-700 dark:text-yellow-300">
 {rolesError}
 </p>
 </div>
 </div>
 )}

 {/* Name */}
 <ModernInput
  label={t('userRegistration.nameLabel')}
  value={formData.name}
  onChange={(e) => handleInputChange('name', e.target.value)}
  placeholder={t('userRegistration.namePlaceholder')}
  error={errors.name}
  leftIcon={<User className="h-5 w-5" />}
  required
 />

 {/* Email */}
 <ModernInput
  label={t('userRegistration.emailLabel')}
  type="email"
  value={formData.email}
  onChange={(e) => handleInputChange('email', e.target.value)}
  placeholder={t('userRegistration.emailPlaceholder')}
  error={errors.email}
  leftIcon={<Mail className="h-5 w-5" />}
  required
 />

 {/* Phone */}
 <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
 <div>
 <ModernInput
  label={t('userRegistration.countryCodeLabel')}
  value={formData.codeArea}
  onChange={(e) => handleInputChange('codeArea', e.target.value)}
  placeholder={t('userRegistration.countryCodePlaceholder')}
  disabled
 />
 </div>
 <div>
 <ModernInput
  label={t('userRegistration.areaLabel')}
  value={formData.area}
  onChange={(e) => handleInputChange('area', e.target.value)}
  placeholder={t('userRegistration.areaPlaceholder')}
  error={errors.area}
  required
 />
 </div>
 <div>
 <ModernInput
 label={t('userRegistration.phoneLabel')}
 value={formData.phone}
 onChange={(e) => handleInputChange('phone', e.target.value)}
 placeholder={t('userRegistration.phonePlaceholder')}
 error={errors.phone}
 required
 />
 </div>
 </div>

 {/* Campo isActive - só no modo de edição */}
 {isEditMode && (
 <div className="space-y-2">
 <label className="block text-sm font-medium text-foreground">
 {t('userRegistration.userStatusLabel')}
 </label>
 <div className="flex items-center space-x-4">
 <label className="flex items-center">
 <input
 type="radio"
 name="isActive"
 checked={formData.isActive === true}
 onChange={() => handleInputChange('isActive', 'true')}
 className="mr-2 text-accent focus:ring-accent"
 />
 <span className="text-sm text-foreground">{t('userRegistration.active')}</span>
 </label>
 <label className="flex items-center">
 <input
 type="radio"
 name="isActive"
 checked={formData.isActive === false}
 onChange={() => handleInputChange('isActive', 'false')}
 className="mr-2 text-accent focus:ring-accent"
 />
 <span className="text-sm text-foreground">{t('userRegistration.inactive')}</span>
 </label>
 </div>
 </div>
 )}

 {/* Role Selection */}
 <div className="space-y-2">
 <label className="block text-sm font-medium text-foreground">
 {t('userRegistration.roleLabel')}
 </label>

 {rolesLoading ? (
 <div className="text-center py-4">
 <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent mx-auto"></div>
 <p className="text-sm text-muted-foreground mt-2">
 {t('userRegistration.loadingRoles')}
 </p>
 </div>
 ) : (
 <div className="space-y-3">
 {roles.map((role) => (
 <label
 key={role.value}
 className={`flex items-start p-4 border rounded-lg cursor-pointer transition-colors ${
 formData.dealershipRole === role.value
 ? 'border-accent bg-accent/5 dark:bg-primary-900/20'
 : 'border-border hover:border-border dark:hover:border-gray-600'
 }`}
 >
 <input
 type="radio"
 name="dealershipRole"
 value={role.value}
 checked={formData.dealershipRole === role.value}
 onChange={(e) => handleInputChange('dealershipRole', e.target.value)}
 className="mt-1 h-4 w-4 text-accent focus:ring-accent border-border"
 />
 <div className="ml-3 flex-1">
 <div className="flex items-center">
 <Shield className="h-5 w-5 text-accent mr-2" />
 <span className="font-medium text-foreground">
 {role.label}
 </span>
 </div>
 {role.description && (
 <p className="text-sm text-muted-foreground mt-1">
 {role.description}
 </p>
 )}
 </div>
 </label>
 ))}
 </div>
 )}
 
 {errors.role && (
 <p className="text-sm text-red-600 dark:text-red-400">
 {errors.role}
 </p>
 )}
 </div>

 {/* Password */}
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <PasswordInput
 label={t('userRegistration.passwordLabel')}
 value={formData.password}
 onChange={(e) => handleInputChange('password', e.target.value)}
 placeholder={t('userRegistration.passwordPlaceholder')}
 error={errors.password}
 required
 />
 <PasswordInput
 label={t('userRegistration.confirmPasswordLabel')}
 value={formData.confirmPassword}
 onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
 placeholder={t('userRegistration.confirmPasswordPlaceholder')}
 error={errors.confirmPassword}
 required
 />
 </div>

 {/* Selected Role Info */}
 {formData.dealershipRole && (
 <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
 <div className="flex items-start">
 <CheckCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
 <div>
 <p className="font-medium text-blue-900 dark:text-blue-100">
 {t('userRegistration.roleInfoPrefix')} {getSelectedRole()?.label}
 </p>
 <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
 {getSelectedRole()?.description}
 </p>
 </div>
 </div>
 </div>
 )}

 {/* Submit Button */}
 <div className="flex justify-end space-x-4">
 <Button
 type="button"
 variant="outline"
 onClick={() => navigate('/users')}
 >
 {t('userRegistration.cancel')}
 </Button>
 <ModernButton
 type="submit"
 loading={isSubmitting}
 className="flex items-center"
 >
{isEditMode ? <Save className="h-4 w-4 mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
{isEditMode ? t('userRegistration.submitEdit') : t('userRegistration.submitCreate')}
 </ModernButton>
 </div>
 </form>
 </CardContent>
 </Card>
 </div>
 )
}
